import { DateTime } from "luxon";

const periods = {
	full: () => DateTime.now().endOf("day").plus({ years: 200 }),
	"3d": () => DateTime.now().endOf("day").plus({ days: 3 }),
	today: () => DateTime.now().endOf("day"),
	"48h": () => DateTime.now().endOf("hour").plus({ hours: 48 }),
	"24h": () => DateTime.now().endOf("hour").plus({ hours: 24 }),
	"3h": () => DateTime.now().endOf("hour").plus({ hours: 3 }),
};

export type PredefinedPeriod = keyof typeof periods;
export type Period = PredefinedPeriod | string;

export const validPeriods: PredefinedPeriod[] = Object.keys(
	periods
) as PredefinedPeriod[];

/** Round time to start of next hour. */
export function roundToStartOfNextHour(dateTime: DateTime) {
	if (dateTime.second !== 0 || dateTime.minute !== 0) {
		return dateTime.startOf("hour").plus({ hours: 1 });
	}
	return dateTime;
}

class PeriodConverter {
	/** Calculates offer interval based on period. */
	static CalculateOfferInterval(period: Period): {
		lt: Date;
		gte?: Date;
	} {
		// A. Handle Predefined Periods ("3h", "today") -> Returns just { lt: ... }
		if (this.IsPredefinedPeriod(period)) {
			let momentTime = periods[period as PredefinedPeriod]();

			if (momentTime.second !== 0 || momentTime.minute !== 0) {
				momentTime = momentTime.startOf("hour").plus({ hours: 1 });
			}

			return {
				lt: momentTime.toUTC().toJSDate(),
			};
		}

		// B. Handle Date Strings ("2025-12-16") -> Returns { gte: ..., lt: ... }
		if (this.IsDateString(period)) {
			const selectedDate = DateTime.fromISO(period);

			if (selectedDate.isValid) {
				return {
					// Start of the day
					gte: selectedDate.startOf("day").toJSDate(),
					// Start of the NEXT day
					lt: selectedDate
						.plus({ days: 1 })
						.startOf("day")
						.toJSDate(),
				};
			}
		}

		// C. Fallback
		return this.CalculateOfferInterval(this.GetDefaultPeriod());
	}

	/** Checks if the period is a valid YYYY-MM-DD string */
	static IsDateString(period: Period): boolean {
		// Check for format "YYYY-MM-DD" and validity
		return (
			typeof period === "string" &&
			/^\d{4}-\d{2}-\d{2}$/.test(period) &&
			DateTime.fromISO(period).isValid
		);
	}

	/** A date-string period selects a historical day → offer-history mode. */
	static IsOfferHistoryPeriod(period: Period): boolean {
		return WEBPACK_IS_OFFER_HISTORY_ENABLED && this.IsDateString(period);
	}
	/** Checks if the period is one of the hardcoded keys */
	static IsPredefinedPeriod(period: Period): boolean {
		return validPeriods.includes(period as PredefinedPeriod);
	}

	/** Calculates offer interval based on period. */
	static IsValidPeriod(period: Period): boolean {
		return this.IsPredefinedPeriod(period) || this.IsDateString(period);
	}

	/** Returns default period. */
	static GetDefaultPeriod(): Period {
		return "full";
	}
}

export default PeriodConverter;
